import React from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Loader2, Link2, RefreshCw, AlertCircle } from 'lucide-react';
import { getAccount, linkAccount } from '../../services/brokerageService';
import type { BrokerageError } from '../../services/brokerageService';

function formatCurrency(amount: number, currency = 'ZAR'): string {
  return new Intl.NumberFormat('en-ZA', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
}


const AccountBalance: React.FC = () => {
  const queryClient = useQueryClient();

  const { data: account, isLoading, isFetching, isError, error, refetch } = useQuery({
    queryKey: ['brokerage', 'account'],
    queryFn: getAccount,
    staleTime: 30_000,
    retry: (failureCount: number, err: unknown) => {
      const brokerageErr = err as BrokerageError;
      if (brokerageErr?.status === 404) return false;
      return failureCount < 2;
    },
  });

  const linkMutation = useMutation({
    mutationFn: () => linkAccount('Demo Brokerage', 'DEMO-ACCOUNT'),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['brokerage', 'account'] });
    },
  });

  const brokerageError = error as BrokerageError | null;
  const isNotLinked =
    isError &&
    (brokerageError?.status === 404 ||
      brokerageError?.response?.data?.error?.code === 'account_not_linked');

  if (isLoading) {
    return (
      <div className="bg-slate-800/50 rounded-xl border border-slate-700/50 p-5 flex items-center gap-3">
        <Loader2 size={18} className="animate-spin text-teal-400" />
        <span className="text-slate-400 text-sm">Loading balance…</span>
      </div>
    );
  }

  if (isNotLinked) {
    return (
      <div className="bg-slate-800/50 rounded-xl border border-slate-700/50 p-5">
        <p className="text-sm text-slate-400 mb-3">No brokerage account linked</p>
        <button
          onClick={() => linkMutation.mutate()}
          disabled={linkMutation.isPending}
          className="flex items-center gap-2 px-4 py-2 bg-teal-600 hover:bg-teal-500 disabled:opacity-60 text-white text-sm font-semibold rounded-lg transition-colors"
        >
          {linkMutation.isPending ? (
            <Loader2 size={14} className="animate-spin" />
          ) : (
            <Link2 size={14} />
          )}
          Link your brokerage account
        </button>
        {linkMutation.isError && (
          <p className="text-xs text-red-400 mt-2">Failed to link account. Please try again.</p>
        )}
      </div>
    );
  }

  if (isError) {
    return (
      <div className="bg-slate-800/50 rounded-xl border border-slate-700/50 p-5">
        <div className="flex items-center gap-2 text-red-400 text-sm mb-3">
          <AlertCircle size={16} />
          <span>Unable to fetch balance</span>
        </div>
        <button
          onClick={() => refetch()}
          className="flex items-center gap-2 px-3 py-1.5 bg-slate-700 hover:bg-slate-600 text-slate-300 text-xs rounded-lg transition-colors"
        >
          <RefreshCw size={12} />
          Retry
        </button>
      </div>
    );
  }

  if (!account) return null;

  return (
    <div className="bg-slate-800/50 rounded-xl border border-slate-700/50 p-5">
      <div className="flex items-start justify-between mb-2">
        <span className="text-xs text-slate-500 uppercase tracking-wide">Available Cash</span>
        {isFetching && (
          <Loader2 size={12} className="animate-spin text-slate-500" />
        )}
      </div>
      <p className="text-2xl font-semibold text-slate-100">
        {formatCurrency(account.available_cash, account.currency || 'ZAR')}
      </p>
      {account.account_id_masked && (
        <p className="text-xs text-slate-500 mt-1">Account {account.account_id_masked}</p>
      )}
    </div>
  );
};

export default AccountBalance;
